import { css } from "@emotion/css";
import { getDaysInMonth } from "date-fns";
import React, { useEffect, useMemo, useState } from "react";
import { useLocale } from "react-aria";
import { Item } from "react-stately";

import { DateSegmentProps, DateSelectProps } from "./types";
import { FormFeedback } from "../../../components/inputs/_assets/input-wrapper/FormFeedback";
import { FormLabel } from "../../../components/inputs/_assets/input-wrapper/FormLabel";
import { Select } from "../../../deprecated/select/Select";
import { useDateFNSLocale } from "../../../hooks/useDateFNSLocale";

const now = new Date();
const currentYear = now.getUTCFullYear();

/**
 * @deprecated use DateSelectRAC instead
 */
export function DateSelect(props: DateSelectProps) {
  const { value = { day: 0, month: 0, year: 0 } } = props;
  const { locale } = useLocale();
  const localeValue = props.locale ?? locale;
  const { dateFNSLocale } = useDateFNSLocale(localeValue as any);
  const [formatOrder, setFormatOrder] = React.useState<string[]>([]);
  const [focused, setFocused] = useState<Record<string, boolean | null>>({
    year: null,
    month: null,
    day: null,
  });

  const displayNames = new Intl.DisplayNames(localeValue, {
    type: "dateTimeField",
  });

  const yearPlaceholder = displayNames.of("year") as string;
  const monthPlaceholder = displayNames.of("month") as string;
  const dayPlaceholder = displayNames.of("day") as string;

  useEffect(() => {
    const componentIsTouched = Object.values(focused).some(
      (focus) => focus !== null
    );
    const componentIsFocused = Object.values(focused).some(
      (focus) => focus === true
    );

    if (componentIsFocused) {
      props.onFocus && props.onFocus();
    } else if (componentIsTouched) {
      props.onBlur && props.onBlur();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focused]);

  const handleSetYear = (year: number) => {
    const daysInMonth = fetchDaysInMonth(value.month, value.year);

    props.onChange({
      ...value,
      year,
      day: value.day > daysInMonth ? 0 : value.day,
    });
  };

  const handleSetMonth = (month: number) => {
    const daysInMonth = fetchDaysInMonth(month, value.year);

    props.onChange({
      ...value,
      month,
      day: value.day > daysInMonth ? 0 : value.day,
    });
  };

  const handleSetDay = (day: number) => {
    props.onChange({ ...value, day });
  };

  const fetchDaysInMonth = (month: number, year: number) => {
    if (!month && !year) return 0;
    return getDaysInMonth(new Date(year, month - 1, 1));
  };

  // set formatOrder based on override or locale language specific ordering
  useEffect(() => {
    const format = props.formatOverride
      ? props.formatOverride.toLowerCase()
      : dateFNSLocale?.formatLong?.date({ width: "short" }).toLowerCase();

    // remove whitespaces and split by various locale seperating chars
    // ex: "hu-HU": "yyyy. MM. dd." or "es-PA": "MM/dd/yyyy"
    setFormatOrder(
      format
        .replace(/\s/g, "") // remove spaces
        .replace(/([ymd])(?=.*\1)/g, "") // remove repeated characters
        .split(/[.,/-]/) // split into array for ordering
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.formatOverride, localeValue]);

  // calculates and fills array with 100 based on isFutureDate props
  const years = useMemo(() => {
    return Array.from({ length: 100 }, (_, idx) => {
      if (props.isFutureDate) return currentYear + idx;
      else return currentYear - idx;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localeValue]);

  // builds month array with locale specific months
  const months = useMemo(
    () =>
      Array.from({ length: 12 }, (_, idx) =>
        dateFNSLocale?.localize?.month(idx)
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [localeValue]
  );

  // builds days array based on month and year selected
  const days = useMemo(() => {
    const daysInMonth =
      value.year && value.month
        ? fetchDaysInMonth(value.month, value.year)
        : 31;

    return Array.from({ length: daysInMonth }, (_, idx) => idx + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localeValue, value]);

  const segmentLookup = (input: string, index: number) => {
    switch (input) {
      case "m":
        return (
          <div
            key={index}
            className={styles.month}
            aria-label={`${props.label} ${monthPlaceholder}`}
          >
            <DateSelectSegment
              testID={`${props.testID}-month`}
              isDisabled={props.isDisabled}
              selectedKey={value?.month}
              input={input}
              setValue={handleSetMonth}
              options={months}
              placeholder={
                monthPlaceholder?.charAt(0).toUpperCase() +
                monthPlaceholder?.slice(1)
              }
              hasError={!!props.errorMessage && !value.month}
              onFocus={() => setFocused((curr) => ({ ...curr, month: true }))}
              onBlur={() =>
                setTimeout(() =>
                  setFocused((curr) => ({ ...curr, month: false }))
                )
              }
            />
          </div>
        );
      case "d":
        if (props.isApproximateDate) return null;
        return (
          <div
            key={index}
            className={styles.day}
            aria-label={`${props.label} ${dayPlaceholder}`}
          >
            <DateSelectSegment
              testID={`${props.testID}-day`}
              isDisabled={props.isDisabled}
              selectedKey={value.day}
              input={input}
              setValue={handleSetDay}
              options={days}
              placeholder={
                props.isApproximateDate
                  ? "hidden"
                  : dayPlaceholder?.charAt(0).toUpperCase() +
                    dayPlaceholder?.slice(1)
              }
              hasError={!!props.errorMessage && !value.day}
              onFocus={() => setFocused((curr) => ({ ...curr, day: true }))}
              onBlur={() =>
                setTimeout(() =>
                  setFocused((curr) => ({ ...curr, day: false }))
                )
              }
            />
          </div>
        );
      case "y":
        return (
          <div
            key={index}
            className={styles.year}
            aria-label={`${props.label} ${yearPlaceholder}`}
          >
            <DateSelectSegment
              testID={`${props.testID}-year`}
              isDisabled={props.isDisabled}
              selectedKey={value.year}
              input={input}
              setValue={handleSetYear}
              options={years}
              placeholder={
                yearPlaceholder?.charAt(0).toUpperCase() +
                yearPlaceholder?.slice(1)
              }
              hasError={!!props.errorMessage && !value.year}
              onFocus={() => setFocused((curr) => ({ ...curr, year: true }))}
              onBlur={() =>
                setTimeout(() =>
                  setFocused((curr) => ({ ...curr, year: false }))
                )
              }
            />
          </div>
        );
    }
  };

  return (
    <div>
      <FormLabel isRequired={props.isRequired}>{props.label}</FormLabel>
      <div className={styles.wrapper}>
        {formatOrder.map((input: string, index: number) =>
          segmentLookup(input, index)
        )}
      </div>
      <FormFeedback>{props.errorMessage}</FormFeedback>
    </div>
  );
}

const DateSelectSegment = (props: DateSegmentProps) => {
  const { selectedKey, input, setValue, options, placeholder, ...rest } = props;

  return (
    <div className={styles.segment}>
      <Select
        selectedKey={selectedKey ? selectedKey.toString() : null}
        aria-label={placeholder}
        placeholder={placeholder}
        onSelectionChange={(key) => setValue(Number(key))}
        onBlur={props.onBlur}
        onFocus={props.onFocus}
        {...rest}
      >
        {options.map((item: number, index: number) => {
          // we don't want to submit February in french as a value
          // we also want index 0 to be day 1 and month 1
          let value = item;
          if (["m", "d"].includes(input)) value = index + 1;
          return (
            <Item
              aria-label={item.toString()}
              textValue={item.toString()}
              key={value}
              data-testid="select-option"
            >
              {item}
            </Item>
          );
        })}
      </Select>
    </div>
  );
};

const styles = {
  segment: css({
    minWidth: "76px",
  }),
  wrapper: css({
    width: "100%",
    display: "flex",
    gap: "var(--space-2)",
  }),
  year: css({
    flex: "1.25",
    minWidth: "76px",
  }),
  month: css({
    flex: "2",
  }),
  day: css({
    minWidth: "76px",
  }),
};
